import { useMemo, useState } from 'react';

import { Button } from 'components/button/Button';
import { Dialog } from 'components/dialog/Dialog';
import { Input } from 'components/input/Input';
import { useTranslation } from 'contexts/translation-context';
import { getDisplayError } from '@utils/get-display-error';
import { displayTrailer, type TrailerOption } from 'src/app/trailer/displayTrailer';

import { linkTelemetryVehicle, unlinkTelemetryVehicle, useCompanyVehicles, type TelemetryVehicle } from '../models';
import { labelOf, linkedNameOf } from '../vehicle-labels';
import { VehicleLinkList } from './VehicleLinkList';

interface ILinkTrackerDialogProps {
  /** The trailer every picked unit is linked to. */
  trailer: TrailerOption;
  onClose: () => void;
  onLinked: () => void | Promise<void>;
}

/**
 * The trailer page's "Koppel tracker": which of the fleet's reported units sits in this trailer.
 *
 * The mirror of `LinkVehiclesDialog` rather than a mode of it, because the two start at opposite ends —
 * that one holds a unit and asks for its target, this one holds a trailer and asks for its unit. What they
 * share is the row, the link call and the wording, not the shape of their steps: here there is one step,
 * because the target is the trailer the user is already looking at.
 *
 * The fleet means every connection at once — the user has one fleet, however many provider accounts it is
 * spread across. It is fetched by this dialog and not by the page, so a trailer nobody is editing costs
 * nothing, and the dialog is mounted only while it is open.
 */
export const LinkTrackerDialog = ({ trailer, onClose, onLinked }: ILinkTrackerDialogProps) => {
  const { i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { data, isLoading } = useCompanyVehicles();

  const units = useMemo(() => {
    const term = search.trim().toLowerCase();
    const reported = data ?? [];

    // The name a row shows once it is linked is searchable too: the user reads "Frigo 1" in the row and
    // types exactly that, and a unit whose own name is a bare plate has nothing else to go on.
    return term
      ? reported.filter(
          (vehicle) =>
            labelOf(vehicle).toLowerCase().includes(term) || linkedNameOf(vehicle).toLowerCase().includes(term),
        )
      : reported;
  }, [data, search]);

  const run = async (vehicle: TelemetryVehicle, action: () => Promise<unknown>) => {
    setPendingId(vehicle.id);
    setError('');
    try {
      await action();
      await onLinked();
      onClose();
    } catch (err) {
      setError(getDisplayError(err));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Dialog isOpen onClose={onClose} title={`${i18n('liveTracking.linkTracker')} — ${displayTrailer(trailer)}`}>
      <div className="grid gap-4">
        <p className="text-sm text-fg-muted">{i18n('liveTracking.linkTrackerHint')}</p>

        {/* Worth a search box because this list is the whole fleet: a trailer's unit is found by its name,
            and a Suivo unit's name is not its trailer's plate. */}
        <Input type="text" value={search} onChange={setSearch} placeholder={i18n('liveTracking.searchUnits')} />

        <VehicleLinkList
          vehicles={units}
          isLoading={isLoading}
          pendingId={pendingId}
          onPick={(vehicle) => void run(vehicle, () => linkTelemetryVehicle(vehicle.id, { trailerId: trailer.id }))}
          // Only this trailer's own unit can be detached from here: another trailer's is not this dialog's
          // business, and unlinking it would be a surprise.
          canUnlink={(vehicle) => vehicle.trailerId === trailer.id}
          onUnlink={(vehicle) => void run(vehicle, () => unlinkTelemetryVehicle(vehicle.id))}
        />

        {error && <div className="text-sm text-negative-text">{error}</div>}

        <div className="flex justify-end">
          <Button onTrigger={onClose}>{i18n('cancel')}</Button>
        </div>
      </div>
    </Dialog>
  );
};
